/**
 * Sessions scoped to a stage's date (backend
 * `/api/fleet/events/<id>/stages/<i>/sessions`).
 *
 * MVP linkage for Epic #93 / #94: the backend returns this event's
 * sessions recorded on the stage's calendar date (the stage's
 * stages_json `date`, else the event's first active day), resolved in
 * the event timezone. Read-only; refreshes every 30 s like useEventRuns
 * (a session only joins when a device records anyway).
 */

import { useCallback, useEffect, useState } from 'react';

import { StageSession, listStageSessions } from '../services/fleet';

const REFRESH_MS = 30_000;

export interface StageSessionsState {
  sessions: StageSession[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useStageSessions(
  eventId: number | null,
  stageIndex: number | null,
): StageSessionsState {
  const [sessions, setSessions] = useState<StageSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [epoch, setEpoch] = useState(0);
  const reload = useCallback(() => setEpoch((e) => e + 1), []);

  useEffect(() => {
    if (eventId == null || stageIndex == null) {
      setSessions([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const data = await listStageSessions(eventId, stageIndex);
        if (!cancelled) {
          setSessions(data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setSessions([]);
          setError(err instanceof Error ? err.message : 'failed to load sessions');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    const id = window.setInterval(() => {
      void load();
    }, REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [eventId, stageIndex, epoch]);

  return { sessions, loading, error, reload };
}

export default useStageSessions;
